import {
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Slide,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import React from "react";
import LayoutContainer from "../widgets/LayoutContainer";
import { useMutation } from "react-query";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { API_ENDPOINT } from "../../utils/constants";

const validationSchema = Yup.object().shape({
  orgName: Yup.string()
    .required("Organisation Name is required")
    .max(100, "Maximum 100 characters"),
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email format")
    .max(255, "Maximum 255 characters"),
  phone: Yup.string()
    .required("Phone is required")
    .max(10, "Maximum 10 characters"),
  contactPerson: Yup.string()
    .required("Contact Person is required")
    .max(100, "Maximum 100 characters"),
  address: Yup.string()
    .required("Address is required")
    .max(255, "Maximum 255 characters"),
  message: Yup.string()
    .required("Message is required")
    .max(1000, "Maximum 1000 characters"),
});

const BookTrial = () => {
  const [submitError, setSubmitError] = React.useState("");
  const [submitSuccess, setSubmitSuccess] = React.useState("");
  const [openDialog, setOpenDialog] = React.useState(false);

  const submitForm = async (values) => {
    const response = await fetch(
      `${API_ENDPOINT}user/register-organiser?pb=betazen`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...values,
          // Trim all values before sending
          orgName: values.orgName.trim(),
          email: values.email.trim(),
          phone: values.phone.trim(),
          contactPerson: values.contactPerson.trim(),
          address: values.address.trim(),
          message: values.message.trim(),
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Submission failed");
    }
    return response.json();
  };

  const { mutateAsync, isLoading } = useMutation(submitForm, {
    onSuccess: (response) => {
      setSubmitSuccess(response?.message || "Form submitted successfully");
      setSubmitError("");
      setOpenDialog(true);
    },
    onError: (error) => {
      setSubmitError(error?.message || "An error occurred");
      setSubmitSuccess(false);
    },
  });

  return (
    <LayoutContainer>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Book your Trial Version!
        </Typography>
        <Typography variant="body1" gutterBottom mb={2}>
          Welcome! Please provide your details below to get access to the Trial.
        </Typography>

        <Formik
          initialValues={{
            orgName: "",
            email: "",
            phone: "",
            contactPerson: "",
            address: "",
            message: "",
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            try {
              await mutateAsync(values);
              resetForm();
            } catch (error) {
              // Error handled by mutation
            }
          }}
        >
          {({ errors, touched }) => (
            <Form>
              <Stack spacing={3}>
                <Field
                  as={TextField}
                  name="orgName"
                  label="Organisation Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={touched.orgName && !!errors.orgName}
                  helperText={touched.orgName && errors.orgName}
                />

                <Field
                  as={TextField}
                  name="email"
                  size="small"
                  label="Email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                />

                <Field
                  as={TextField}
                  size="small"
                  name="phone"
                  label="Phone"
                  variant="outlined"
                  fullWidth
                  type="number"
                  error={touched.phone && !!errors.phone}
                  helperText={touched.phone && errors.phone}
                />

                <Field
                  as={TextField}
                  size="small"
                  name="contactPerson"
                  label="Contact Person"
                  variant="outlined"
                  fullWidth
                  error={touched.contactPerson && !!errors.contactPerson}
                  helperText={touched.contactPerson && errors.contactPerson}
                />

                <Field
                  as={TextField}
                  name="address"
                  size="small"
                  label="Address"
                  variant="outlined"
                  fullWidth
                  error={touched.address && !!errors.address}
                  helperText={touched.address && errors.address}
                />

                <Field
                  as={TextField}
                  name="message"
                  label="Please provide your requirements so we can process your trial account request efficiently."
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  error={touched.message && !!errors.message}
                  helperText={touched.message && errors.message}
                />

                {submitError && (
                  <Alert severity="error" sx={{ width: "100%" }}>
                    {submitError}
                  </Alert>
                )}

                {submitSuccess && (
                  <Alert severity="success" sx={{ width: "100%" }}>
                    {submitSuccess}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {isLoading ? "Submitting..." : "Submit Request"}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          TransitionComponent={Slide}
          TransitionProps={{ direction: "up" }}
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "16px",
              background: "linear-gradient(145deg, #f5f7fa 0%, #c3cfe2 100%)",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              py: 4,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: "60px",
                height: "4px",
                backgroundColor: "primary.main",
                borderRadius: "2px",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <CheckCircleOutlinedIcon
                sx={{
                  fontSize: 64,
                  color: "success.main",
                  mr: 2,
                  animation: "bounce 1s ease",
                  "@keyframes bounce": {
                    "0%, 20%, 50%, 80%, 100%": { transform: "translateY(0)" },
                    "40%": { transform: "translateY(-20px)" },
                    "60%": { transform: "translateY(-10px)" },
                  },
                }}
              />
            </Box>
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                mt: 1,
              }}
            >
              Request Successfully Submitted!
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ px: 4, py: 3 }}>
            <Box
              sx={{
                textAlign: "center",
                "& p": {
                  fontSize: "1.1rem",
                  lineHeight: 1.6,
                  mb: 2,
                },
              }}
            >
              <Typography variant="body1" sx={{ color: "text.primary" }}>
                🎉 You've taken the first step! We've sent a confirmation email
                to your inbox.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 2 }}
              >
                Our team is reviewing your request and you'll receive trial
                access details within 24 hours. Check your spam folder if you
                don't see our email.
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 4,
              py: 3,
              justifyContent: "center",
              "& .MuiButton-root": {
                minWidth: "120px",
                borderRadius: "8px",
                py: 1,
                px: 4,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                },
              },
            }}
          >
            <Button
              onClick={() => setOpenDialog(false)}
              variant="contained"
              color="primary"
              size="large"
              sx={{
                fontWeight: 600,
                boxShadow: 2,
                "&:hover": {
                  boxShadow: 4,
                },
              }}
            >
              Got It!
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LayoutContainer>
  );
};

export default BookTrial;
